import {
    ICompleteActionPostLoginModel,
    IPostLoginChannel,
    ITopicSubscription,
    MessageBroker,
    PostLoginActionNameEnum,
} from '@sparkware/uc-sdk-core';
import Container from 'typedi';
import DeferredObject from '../../../../Modules/Utils/DeferredObject';
import { ILogger, LoggerProvider } from '../../logger';
import { Utils } from '../../utils';
import { IPostLoginAction } from '../models/IPostLoginAction';
import { IPostLoginActionsState, PostLoginActionType } from '../models/IPostLoginActionsState';
import { PostLoginEventEmitter } from '../post-login.eventEmitter';
import { PostLoginStorage } from '../post-login.storage';

export abstract class PostLoginActionBase implements IPostLoginAction {
    private readonly _postLoginEventEmitter: PostLoginEventEmitter;
    private readonly _storage: PostLoginStorage;
    protected readonly actionName: PostLoginActionNameEnum;
    protected readonly postLoginChannel: IPostLoginChannel;
    protected readonly utils: Utils;

    private _actionExecutionDO: DeferredObject<void>;
    private _postLoginCompletionSubscription: ITopicSubscription;
    protected _logger: ILogger;

    private _state: IPostLoginActionsState;
    private _options: PostLoginActionType;

    constructor(
        actionName: PostLoginActionNameEnum,
        loggerName: string,
        state: IPostLoginActionsState,
    ) {
        this.actionName = actionName;
        this.postLoginChannel = MessageBroker.getInstance().postLogin;
        this._logger = Container.get(LoggerProvider).getLogger(loggerName);
        this.utils = Container.get(Utils);
        this._storage = Container.get(PostLoginStorage);
        this._state = state;
        this._options = state.actionsOptions?.[actionName];
        this._postLoginEventEmitter = Container.get(PostLoginEventEmitter);
    }

    public execute = async (): Promise<void> => {
        this._postLoginEventEmitter.sendActionStartEvent(
            this.actionName,
            this._state.correlationID,
        );
        if (await this.canExecute(this._options)) {
            const completionObjectPromise = this._createCompletionObject();

            await this.doAction(this._options);

            await completionObjectPromise;
        } else {
            this.completeAction();
        }

        this._postLoginEventEmitter.sendActionCompleteEvent(
            this.actionName,
            this._state.correlationID,
        );
    };

    protected canExecute = (options?: PostLoginActionType): Promise<boolean> => {
        return Promise.resolve(true);
    };

    protected doAction: (options?: PostLoginActionType) => Promise<void>;

    protected completeAction = () =>
        this._completeAction(
            this._postLoginCompletionSubscription,
            this._actionExecutionDO,
        )(this.actionName);

    protected clearOptions = () => {
        this._storage.updateActionOptions(this.actionName, null);
    };

    protected wasFlowInterrupted = () => this._state.interrupted;

    private _completeAction =
        (sub: ITopicSubscription, executionDO: DeferredObject<void>) =>
        (actionName: PostLoginActionNameEnum) => {
            this._logger.log(
                `_completeAction: ${this.actionName}: starting completion of the action named: ${actionName}`,
            );

            if (actionName === this.actionName) {
                sub?.unsubscribe();
                executionDO?.resolve();

                this._logger.log(`_completeAction: ${this.actionName}: action was completed.`);

                return;
            }

            this._logger.log(
                `_completeAction: ${this.actionName}: action was not completed because of action name mismatch. current action name = ${this.actionName}, received action name: ${actionName}.`,
            );
        };

    private _createCompletionObject = async () => {
        this._actionExecutionDO = new DeferredObject<void>();
        this._postLoginCompletionSubscription =
            this.postLoginChannel.topics.completeAction.subscribe(
                (data: ICompleteActionPostLoginModel) => {
                    this._logger.log(
                        `createCompletionObject: ${
                            this.actionName
                        }: completeAction subscription: Received message to complete action with the following name: ${
                            data?.actionName || 'undefined'
                        }`,
                    );
                    this._completeAction(
                        this._postLoginCompletionSubscription,
                        this._actionExecutionDO,
                    )(data?.actionName);
                },
            );

        return this._actionExecutionDO.promise;
    };
}
